import { CSSProperties, useMemo, useState } from 'react';
import useWidthBreakpoint from '../../../../../hooks/useWidthBreakpoint';
import { ChartType } from '../../../../ProfitCalculations/types';
import { useResultData } from '../context';
import { tokens } from '../tokens';
import { pick } from '../responsive';
import { buildFooterModel, FooterModel } from '../derived/aggregations';
import EditorialHeader from './shared/EditorialHeader';
import EquityCurve from './charts/EquityCurve';
import { getBotColor } from '../../../../Overview/ClusterItem/Charts/helpers';
import { isBalanceChange, isDistribution } from '../derived/format';
import { applyOutperformance } from '../derived/chartMode';

// X-axis lines — the footer dividers (and a match for the chart's vertical
// gridlines): brighter than a faint hairline, dimmer than the hover cursor.
const AXIS_LINE = '1px solid rgba(255, 255, 255, 0.15)';

const Toggle = ({
  label,
  color,
  on,
  onClick,
}: {
  label: string;
  color: string;
  on: boolean;
  onClick: () => void;
}) => (
  <span
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontSize: 12,
      color: on ? tokens.textPrimary : tokens.textFaint,
      fontFamily: tokens.fontSans,
      cursor: 'pointer',
      userSelect: 'none',
    }}
  >
    <span
      style={{
        width: 12,
        height: 12,
        borderRadius: 2,
        background: on ? color : 'transparent',
        border: `1.5px solid ${on ? color : tokens.textFaint}`,
      }}
    />
    {label}
  </span>
);

const ProfitChart = () => {
  const { breakpoint } = useWidthBreakpoint();
  const { raw, subscriptionView } = useResultData();
  const assetColor = getBotColor(raw.baseAsset) || tokens.textPrimary;

  // Width of the chart's left y-axis (0 when there is only one axis); the
  // legend is shifted right by this so it never overlaps the axis labels.
  const [leftAxisWidth, setLeftAxisWidth] = useState(0);

  // Plot rectangle + time domain reported by the chart — drives the adaptive
  // x-axis footer so its cells line up under the chart's vertical gridlines.
  const [timeAxisInfo, setTimeAxisInfo] = useState<{
    paddingLeft: number;
    paddingRight: number;
    domainStart: number;
    domainEnd: number;
  } | null>(null);

  const [selectedChartTypes, setSelectedChartTypes] = useState<ChartType[]>([
    ChartType.StableBalance,
    ChartType.NoAlgoritmicProgression,
  ]);
  // Not a `ChartType`: it REPLACES the series rather than joining them, so it
  // cannot live in the same list the others are picked from.
  const [showOutperformance, setShowOutperformance] = useState(false);
  const [txToggles, setTxToggles] = useState({
    core: true,
    takeProfit: false,
    stopLoss: false,
    distribution: true,
    balance: true,
  });

  // Custom-transaction dots only exist on the subscription page, so their
  // legend entries only appear when the subscription actually has some.
  const customTransactions = raw.customTransactions ?? [];
  const hasDistribution = customTransactions.some((tx) =>
    isDistribution(tx.transactionType),
  );
  const hasBalanceChange = customTransactions.some((tx) =>
    isBalanceChange(tx.transactionType),
  );

  const toggleChartType = (chartType: ChartType) => {
    setSelectedChartTypes((prev) => {
      if (!prev.includes(chartType)) {
        return [...prev, chartType];
      }
      // Stable / Asset / Price: at least one series must stay selected.
      const next = prev.filter((type) => type !== chartType);
      return next.length === 0 ? prev : next;
    });
  };
  const toggleTx = (key: keyof typeof txToggles) => {
    setTxToggles((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // The series as drawn. Computed HERE and handed to the chart rather than
  // derived on both sides, so the footer can never describe a different line
  // than the one drawn above it.
  const modeHistories = useMemo(
    () =>
      applyOutperformance(
        {
          stable: raw.endBalanceInUsdtHistory,
          asset: raw.endBalanceInAssetHistory,
          nap: raw.priceHistory,
        },
        showOutperformance,
      ),
    [raw, showOutperformance],
  );

  // Adaptive x-axis footer. It follows the visible series: the stable balance
  // takes precedence, and the asset one is used when only that is on.
  //
  // Cells are percentage growth per interval, and the outperformance series
  // crosses zero — `intervalGrowth` states the absolute change for any interval
  // opening at or below zero, since a percentage there has no usable base.
  const footerModel = useMemo<FooterModel | null>(() => {
    if (!timeAxisInfo) {
      return null;
    }
    // Outperformance draws one line and it lives in the stable slot, so the
    // asset history is empty there and must never be picked.
    const useAssetHistory =
      !showOutperformance &&
      !selectedChartTypes.includes(ChartType.StableBalance) &&
      selectedChartTypes.includes(ChartType.AssetBalance);
    return buildFooterModel(
      useAssetHistory ? modeHistories.asset : modeHistories.stable,
      timeAxisInfo.domainStart,
      timeAxisInfo.domainEnd,
    );
  }, [timeAxisInfo, selectedChartTypes, showOutperformance, modeHistories]);

  // Shrink the footer labels as the interval count grows so they never crowd.
  const footerCellCount = footerModel?.cells.length ?? 0;
  const footerLabelSize =
    footerCellCount > 16
      ? 9
      : footerCellCount > 10
      ? 10
      : footerCellCount > 6
      ? 11
      : 12;
  const footerGrowthSize =
    footerCellCount > 16
      ? 11
      : footerCellCount > 10
      ? 14
      : footerCellCount > 6
      ? 18
      : 24;
  const footerCellPadX = footerCellCount > 10 ? 3 : footerCellCount > 6 ? 5 : 8;

  const padding = pick(breakpoint, {
    sm: '4px 0 16px',
    md: '4px 0 16px',
    lg: '4px 0 20px',
  });
  const chartHeight = pick(breakpoint, { sm: 300, md: 400, lg: 480 });

  const legendStyle: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: {
      position: 'static',
      marginBottom: 12,
      background: 'rgba(32,32,32,0.95)',
      border: '1px solid rgba(255,255,255,0.18)',
      borderRadius: 6,
      padding: '12px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
    md: {
      position: 'absolute',
      left: 16 + leftAxisWidth,
      top: 16,
      zIndex: 2,
      background: 'rgba(32,32,32,0.95)',
      border: '1px solid rgba(255,255,255,0.18)',
      borderRadius: 6,
      padding: '10px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      maxWidth: '70%',
    },
    lg: {
      position: 'absolute',
      left: 16 + leftAxisWidth,
      top: 16,
      zIndex: 2,
      background: 'rgba(32,32,32,0.95)',
      border: '1px solid rgba(255,255,255,0.18)',
      borderRadius: 6,
      padding: '12px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
  });

  const renderLegend = () => (
    <div style={legendStyle}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Toggle
          label="Stable Balance"
          color={tokens.green}
          on={selectedChartTypes.includes(ChartType.StableBalance)}
          onClick={() => toggleChartType(ChartType.StableBalance)}
        />
        <Toggle
          label="Asset Balance"
          color={assetColor}
          on={selectedChartTypes.includes(ChartType.AssetBalance)}
          onClick={() => toggleChartType(ChartType.AssetBalance)}
        />
        <Toggle
          label="Non-Algorithmic Difference"
          color={tokens.textTertiary}
          on={selectedChartTypes.includes(ChartType.NoAlgoritmicProgression)}
          onClick={() => toggleChartType(ChartType.NoAlgoritmicProgression)}
        />
        {/* Sits with the series toggles but is not one of them: switching it on
         *  REPLACES the chart with the single outperformance line, so the three
         *  above keep their state and pick nothing until it goes off again.
         *  Coloured to match the line it draws. */}
        <Toggle
          label="Outperformance"
          color={tokens.green}
          on={showOutperformance}
          onClick={() => setShowOutperformance((prev) => !prev)}
        />
      </div>
      <div
        style={{
          borderTop: '1px solid rgba(255,255,255,0.12)',
          paddingTop: 10,
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <Toggle
          label="Core Transaction"
          color={tokens.textPrimary}
          on={txToggles.core}
          onClick={() => toggleTx('core')}
        />
        <Toggle
          label="Take Profit"
          color={tokens.yellow}
          on={txToggles.takeProfit}
          onClick={() => toggleTx('takeProfit')}
        />
        <Toggle
          label="Stop Loss"
          color={tokens.blue}
          on={txToggles.stopLoss}
          onClick={() => toggleTx('stopLoss')}
        />
        {hasDistribution && (
          <Toggle
            label="Distribution"
            color={tokens.amber}
            on={txToggles.distribution}
            onClick={() => toggleTx('distribution')}
          />
        )}
        {hasBalanceChange && (
          <Toggle
            label="Balance Change"
            color={tokens.blue}
            on={txToggles.balance}
            onClick={() => toggleTx('balance')}
          />
        )}
      </div>
    </div>
  );

  return (
    <div
      style={{
        // Transparent so the subscription page's profit wash shows behind the
        // chart's top (on the dev Result page the page bg is already #000).
        background: 'transparent',
        padding,
        color: tokens.textPrimary,
        fontFamily: tokens.fontSans,
        maxWidth: 1400,
        margin: '0 auto',
      }}
    >
      <EditorialHeader
        number="01"
        label="The Journey"
        accent={!subscriptionView}
      />

      {breakpoint === 'sm' && renderLegend()}

      <div style={{ position: 'relative', height: chartHeight }}>
        <EquityCurve
          height={chartHeight}
          selectedChartTypes={selectedChartTypes}
          onSelectedChartTypesChange={setSelectedChartTypes}
          showOutperformance={showOutperformance}
          histories={modeHistories}
          transactionTypeToggleValues={txToggles}
          onTransactionTypeToggleValuesChange={setTxToggles}
          onLeftAxisWidthChange={setLeftAxisWidth}
          onTimeAxisChange={setTimeAxisInfo}
        />

        {breakpoint !== 'sm' && renderLegend()}
      </div>

      {/* Adaptive x-axis: one cell per interval. The inner row is inset by
       * the chart's plot padding so every cell divider falls exactly under
       * the chart's vertical gridlines. */}
      <div style={{ minHeight: 64 }}>
        {footerModel && timeAxisInfo && (
          <div
            style={{
              paddingLeft: timeAxisInfo.paddingLeft,
              paddingRight: timeAxisInfo.paddingRight,
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: footerModel.cells
                  .map((cell) => `${cell.weight}fr`)
                  .join(' '),
                borderTop: AXIS_LINE,
                marginTop: 4,
              }}
            >
              {footerModel.cells.map((cell, index) => {
                // The first and last intervals are partial, so their cells
                // would be thin slivers. Stretch them into the chart's side
                // margins (negative margin) so the label keeps room to sit
                // centred — the divider stays locked on the gridline.
                const isFirstCell = index === 0;
                const isLastCell = index === footerModel.cells.length - 1;
                const isEdgeCell = isFirstCell || isLastCell;
                const cellOverflow = isEdgeCell ? 'visible' : 'hidden';
                return (
                  <div
                    key={`${cell.label}_${index}`}
                    style={{
                      padding: `14px ${footerCellPadX}px 6px`,
                      textAlign: 'center',
                      borderRight: isLastCell ? 'none' : AXIS_LINE,
                      minWidth: 0,
                      overflow: cellOverflow,
                      marginLeft: isFirstCell
                        ? -timeAxisInfo.paddingLeft
                        : undefined,
                      marginRight: isLastCell
                        ? -timeAxisInfo.paddingRight
                        : undefined,
                    }}
                  >
                    <div
                      style={{
                        fontSize: footerLabelSize,
                        color: tokens.textMuted,
                        fontFamily: tokens.fontMono,
                        letterSpacing: '0.06em',
                        whiteSpace: 'nowrap',
                        overflow: cellOverflow,
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {cell.label}
                    </div>
                    <div
                      style={{
                        fontSize: footerGrowthSize,
                        fontFamily: tokens.fontDisplay,
                        color: cell.color,
                        marginTop: 4,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        overflow: cellOverflow,
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {cell.growth}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfitChart;
